import { createContext } from 'react';

import type { Signature, Transfer } from '@sorare/crypto';

import type {
  AuthorizationApproval,
  AuthorizationRequest,
} from './authorizations';
import type { Dict } from './dict';
import type { Props } from './provider';
import { MessagingProvider as Provider } from './provider';
import type { MessagingContext as IMessagingContext, RPC } from './types';

type AppHandler =
  | 'changePassword'
  | 'forgotPassword'
  | 'generatedKey'
  | 'getPasskeyEncryptionKey'
  | 'init'
  | 'keys'
  | 'prepareEthDeposit'
  | 'recoverKey'
  | 'resetPassword'
  | 'verify2FA'
  | 'salt'
  | 'signIn'
  | 'signInWithPasskey'
  | 'signUp'
  | 'toggle'
  | 'transaction'
  | 'walletIsLocked'
  | 'requestPlaceholderResize'
  | 'requestOAuth'
  | 'returnToWalletSettingsTab'
  | 'passwordForgotten'
  | 'onBackForgotPassword'
  | 'setMode'
  | 'signUpAdditionalScreen'
  | 'walletLoaded'
  | 'errorTracking'
  | 'invalidNickname';

export type WalletHandler =
  | 'cancelUnlockScreen'
  | 'exportForUser'
  | 'prompt'
  | 'promptSignupWithUsername'
  | 'promptResetPassword'
  | 'promptDeposit'
  | 'promptRestoreWallet'
  | 'password'
  | 'generateKey'
  | 'createWalletRecovery'
  | 'setSolanaAddress'
  | 'loadWallet'
  | 'signTransfer'
  | 'signWalletChallenge'
  | 'signUpMobileView'
  | 'approveAuthorizationRequests'
  | 'approveMigrator'
  | 'signMigration'
  | 'getEip7702Delegation'
  | 'signEip7702Revocation'
  | 'getEscapeHatchSignatures'
  | 'logOut'
  | 'refreshUser'
  | 'backFromSignUpAdditionalScreen';

export type Handler = AppHandler | WalletHandler;

type WalletRPC = RPC<Handler>;

export interface EncryptedPrivateKey {
  salt: string;
  iv: string;
  encryptedPrivateKey: string;
}

interface PasswordEncryptedPrivateKey {
  iv: string;
  payload: string;
  salt: string;
}

export interface PrivateKeyRecovery {
  appId: string;
  encryptionKey: string;
  email?: string;
  iv: string;
  payload: string;
  phone?: string;
  teamId: string;
}

export interface WalletExport {
  ethereumAddress: EthereumAddress;
  starkwarePublicKey: string;
  userKeys: EncryptedPrivateKey;
}

export const backupOwners = ['vaultinum'] as const;

export type BackupOwner = (typeof backupOwners)[number];

export interface BackupPrivateKeyRecovery {
  encryptedSymmetricKey: string;
  iv: string;
  payload: string;
  rsaEncryptionKey: BackupOwner;
  rsaPublicKey: string;
}

export interface PrivateKeyRecoveryPayload {
  iv: string;
  ivTemp: string;
  encryptionKey: string;
  payload: string;
}

export interface Wallet {
  starkKeyWithPrefix: string;
  ethereumAddress: EthereumAddress;
  solanaAddress: string;
  passwordEncryptedPrivateKey: PasswordEncryptedPrivateKey;
  privateKeyRecovery: PrivateKeyRecovery;
  backupPrivateKeyRecoveries: BackupPrivateKeyRecovery[];
  legacyBackupPrivateKeyRecovery?: string;
}

/* APP REQUESTS */

export interface Prompt extends WalletRPC {
  request: {
    handler: 'prompt';
    args: {
      type:
        | 'signup'
        | 'signUpMobileView'
        | 'signIn'
        | 'passwordForgotten'
        | 'changePassword'
        | 'privateKeyExport'
        | 'solanaPrivateKeyExport'
        | 'deposit'
        | 'generateKeys';
    };
  };
}

export interface PromptDeposit extends WalletRPC {
  request: {
    handler: 'promptDeposit';
    args: {
      id?: string;
    };
  };
}

export type Sport = 'FOOTBALL' | 'BASEBALL' | 'NBA';

export interface PromptSignupWithUsername extends WalletRPC {
  request: {
    handler: 'promptSignupWithUsername';
    args: {
      username: string;
      isAndroidApp: boolean;
      sport: Sport;
    };
  };
}

export interface PromptResetPassword extends WalletRPC {
  request: {
    handler: 'promptResetPassword';
    args: {
      email: string;
      nickname: string;
      resetPasswordToken: string;
    };
  };
}

type ResultOrError<T, CODES extends number> =
  | { result: T; error?: never }
  | {
      result?: never;
      error: {
        code: CODES;
        message: string;
      };
    };

export interface PromptRestoreWallet extends WalletRPC {
  request: {
    handler: 'promptRestoreWallet';
    args: {
      privateKeyRecoveryPayload: PrivateKeyRecoveryPayload;
      privateKeyRecoveryPayloads?: PrivateKeyRecoveryPayload[];
    };
  };
}

export enum PasswordError {
  INVALID_PASSWORD = 'InvalidPassword',
}

type PasswordArgs =
  | {
      error?: PasswordError;
      unlockWallet: false;
    }
  | { error?: never; unlockWallet?: never };

export interface Password extends WalletRPC {
  request: {
    handler: 'password';
    args: PasswordArgs;
  };
  response: {
    result: {
      passwordHash?: string;
    };
  };
}

export interface GenerateKey extends WalletRPC {
  request: {
    handler: 'generateKey';
    args: {
      email: string;
    };
  };
  response: {
    result?: {
      wallet: Wallet;
    };
  };
}

export interface CreateWalletRecovery extends WalletRPC {
  request: {
    handler: 'createWalletRecovery';
    args: {
      recoveryMethod: 'email' | 'phone';
      recoveryDestination: string;
    };
  };
  response:
    | {
        result: {
          privateKeyRecovery: PrivateKeyRecovery;
        };
        error?: never;
      }
    | {
        result?: never;
        error: string;
      };
}

export interface SetSolanaAddress extends WalletRPC {
  request: {
    handler: 'setSolanaAddress';
  };
  response:
    | {
        result: { solanaAddress: string };
        error?: never;
      }
    | {
        result?: never;
        error: string;
      };
}

type LoadWalletFailureReason = 'cancelled' | 'noKey' | 'keyFetchFailed';

export interface LoadWallet extends WalletRPC {
  request: {
    handler: 'loadWallet';
  };
  response: {
    result?: boolean;
    error?: string;
    reason?: LoadWalletFailureReason;
  };
}

export interface SignTransfer extends WalletRPC {
  request: {
    handler: 'signTransfer';
    args: {
      transfer: Transfer;
    };
  };
  response: {
    result?: {
      signature: Signature;
    };
  };
}

export interface ApproveAuthorizationRequests extends WalletRPC {
  request: {
    handler: 'approveAuthorizationRequests';
    args: {
      authorizationRequests: AuthorizationRequest[];
    };
  };
  response: ResultOrError<
    {
      authorizationApprovals: AuthorizationApproval[];
      starkKey: string;
    },
    SignLimitOrdersErrorCodes
  >;
}

export interface SignWalletChallenge extends WalletRPC {
  request: {
    handler: 'signWalletChallenge';
    args: {
      challenge: string;
    };
  };
  response: {
    result?: {
      signature: Signature;
    };
  };
}

export enum SignLimitOrdersErrorCodes {
  STARK_KEY_MISMATCH = 0,
  PROMPT_CANCELED = 1,
  MISSING_STARK_KEY = 2,
  SIGNING_ERROR = 3,
  KEY_FETCH_FAILED = 4,
}

export interface ApproveMigrator extends WalletRPC {
  request: {
    handler: 'approveMigrator';
    args: {
      nonce: number | string;
    };
  };
  response: {
    result?: {
      encodedFunction: string;
      signature: string;
      extraGas: number;
    };
  };
}

export interface SignMigration extends WalletRPC {
  request: {
    handler: 'signMigration';
    args: {
      cardIds: string[];
      expirationBlock: number | string;
    };
  };
  response: {
    result?: {
      signature: string;
    };
  };
}

export interface GetEip7702Delegation extends WalletRPC {
  request: {
    handler: 'getEip7702Delegation';
  };
  response: {
    result?: {
      delegated: boolean;
    };
    error?: string;
  };
}

export const EIP_7702_REVOCATION_CANCELLED = 'cancelled';

export interface SignEip7702Revocation extends WalletRPC {
  request: {
    handler: 'signEip7702Revocation';
  };
  response: {
    result?: {
      authorization: {
        chainId: number;
        address: `0x${string}`;
        nonce: number;
        yParity: number;
        r: string;
        s: string;
      };
    };
    error?: string;
  };
}

export interface GetEscapeHatchSignatures extends WalletRPC {
  request: {
    handler: 'getEscapeHatchSignatures';
    args: {
      fee: string;
      feeRecipient: string;
    };
  };
  response: {
    result?: {
      ethAddress: string;
      starkKey: string;
      registerEthAddressSignature: string;
      withdrawWithFeeSignature: string;
      withdrawWithFeeToBaseBankSignature: string;
    };
    error?: string;
  };
}

export interface LogOut extends WalletRPC {
  request: {
    handler: 'logOut';
    args: {
      flushMessagingQueue: boolean;
    };
  };
}

export interface RefreshUser extends WalletRPC {
  request: {
    handler: 'refreshUser';
  };
}

/* WALLET REQUESTS */

interface UserInfo {
  email: string;
  nickname: string;
  hasPassword: boolean;
  address?: string;
  userPrivateKey?: EncryptedPrivateKey;
  starkKey?: string;
  passkeys?: {
    id: string;
    externalId: string;
    publicKey: string;
    sorarePrivateKey: EncryptedPrivateKey | null;
  }[];
}

export interface Toggle extends WalletRPC {
  request: {
    handler: 'toggle';
    args: {
      display: boolean;
      closeDrawer?: boolean;
    };
  };
}

export interface RequestResize extends WalletRPC {
  request: {
    handler: 'requestPlaceholderResize';
    args: {
      height: number;
      width: number;
      swallowIfNoHandler: true;
    };
  };
}

export interface Init extends WalletRPC {
  request: {
    handler: 'init';
  };
  response: {
    result: {
      user?: UserInfo;
      dict: Dict;
      featureFlags: Record<string, string | boolean | number>;
      fiatCurrency: string;
      fiatCurrencySymbol: string;
      ethRate: number;
      langDir?: 'ltr' | 'rtl';
    };
  };
}

export interface Salt extends WalletRPC {
  request: {
    handler: 'salt';
    args: {
      email: string;
    };
  };
  response: {
    result: {
      salt: string;
    };
  };
}

type AcceptTermsInfo = {
  acceptTerms: true;
  acceptAgeLimit: true;
  acceptPrivacyPolicy: true;
  agreedToReceiveOffersFromPartners: boolean;
  agreedToReceiveOffersFromPartnersSpecific?: string[];
};

export interface SignUp extends WalletRPC {
  request: {
    handler: 'signUp';
    args: {
      sport: Sport;
      email: string;
      passwordHash: string;
      nickname: string;
      wallet: Wallet;
      mobile?: boolean;
    } & AcceptTermsInfo;
  };
  response: {
    error?: Record<string, string>;
  };
}

export interface InvalidNickname extends WalletRPC {
  request: {
    handler: 'invalidNickname';
    args: {
      error: string;
    };
  };
}

export interface SignInWithPasskey extends WalletRPC {
  request: {
    handler: 'signInWithPasskey';
  };
  response: {
    result?: UserInfo;
    error?: string;
  };
}

export interface SignIn extends WalletRPC {
  request: {
    handler: 'signIn';
    args: {
      email: string;
      passwordHash: string;
      otpAttempt: string;
      passkeyCredential?: {
        signature: string;
        publicKey: string;
      };
    };
  };
  response: {
    result?: UserInfo;
    error?: string;
  };
}

export interface SignUpMobileView extends WalletRPC {
  request: {
    handler: 'signUpMobileView';
    args: {
      isAndroidApp: boolean;
    };
  };
}

export interface Keys extends WalletRPC {
  request: {
    handler: 'keys';
  };
  response: {
    result: {
      userPrivateKey?: EncryptedPrivateKey;
      sorareEncryptionKey: string;
    };
    /**
     * `invalid-otp`: the key is behind 2FA and an OTP has to be entered, or the
     * one that was entered was rejected.
     * `unconfirmed-device`: the backend does not recognise this device.
     * `key-fetch-failed`: fetching the key failed for any other reason (network
     * error, unexpected GraphQL error, missing payload).
     *
     * All three are distinct from "the user has no key at all", which is
     * reported as a successful response with no `userPrivateKey`.
     */
    error?: 'invalid-otp' | 'unconfirmed-device' | 'key-fetch-failed';
  };
}

export interface PrepareEthDeposit extends WalletRPC {
  request: {
    handler: 'prepareEthDeposit';
    args: {
      weiAmount: bigint;
    };
  };
  response: {
    result?: {
      assetType: string;
      weiAmount: bigint;
      vaultId: number;
    };
  };
}

export interface ChangePassword extends WalletRPC {
  request: {
    handler: 'changePassword';
    args: {
      currentPasswordHash: string;
      passwordHash: string;
      userPrivateKey?: EncryptedPrivateKey;
    };
  };
}

export interface ResetPassword extends WalletRPC {
  request: {
    handler: 'resetPassword';
    args: {
      passwordHash: string;
      resetPasswordToken: string;
    };
  };
  response: {
    error?: {
      resetPasswordToken?: string;
    };
  };
}

export interface Verify2FA extends WalletRPC {
  request: {
    handler: 'verify2FA';
  };
  response: {
    result?: {
      userPrivateKey?: EncryptedPrivateKey;
    };
    error?: 'invalid-otp';
  };
}

export interface GetPasskeyEncryptionKey extends WalletRPC {
  request: {
    handler: 'getPasskeyEncryptionKey';
    args: {
      passkeyIds: string[];
    };
  };
  response: {
    result?: {
      encryptionKey?: BufferSource;
      passkeyId?: string;
    };
  };
}

export interface RecoverKey extends WalletRPC {
  request: {
    handler: 'recoverKey';
    args: {
      passwordHash: string;
      userPrivateKey: EncryptedPrivateKey;
    };
  };
}

export interface GeneratedKey extends WalletRPC {
  request: {
    handler: 'generatedKey';
    args: {
      address?: string;
      passwordHash: string;
      userPrivateKey?: EncryptedPrivateKey;
      userPrivateKeyBackup?: string;
      starkKey?: string;
      wallet: Wallet;
    };
  };
}

export interface ForgotPassword extends WalletRPC {
  request: {
    handler: 'forgotPassword';
    args: {
      email: string;
      remainOpened?: true;
    };
  };
}

export interface OnBackForgotPassword extends WalletRPC {
  request: {
    handler: 'onBackForgotPassword';
  };
}

export interface Transaction extends WalletRPC {
  request: {
    handler: 'transaction';
    args: {
      hash?: string;
      depositId?: string;
      error?: string;
    };
  };
}

export interface ErrorTracking extends WalletRPC {
  request: {
    handler: 'errorTracking';
  };
}
export interface WalletIsLocked extends WalletRPC {
  request: {
    handler: 'walletIsLocked';
    args: {
      isLocked: boolean;
    };
  };
}

export interface CancelUnlockScreen extends WalletRPC {
  request: {
    handler: 'cancelUnlockScreen';
    args: {
      bypassMessagingQueue: boolean;
    };
  };
}

export interface RequestOAuth2 extends WalletRPC {
  request: {
    handler: 'requestOAuth';
    args: {
      signup: boolean;
      platform: 'google' | 'facebook' | 'apple';
      nickname?: string;
      sport?: Sport;
      acceptTerms?: boolean;
      acceptAgeLimit?: boolean;
      acceptToShareAll?: boolean;
      acceptToShareSpecific?: string[];
    };
  };
}

export interface ReturnToWalletSettingsTab extends WalletRPC {
  request: {
    handler: 'returnToWalletSettingsTab';
  };
}

export interface PasswordForgotten extends WalletRPC {
  request: {
    handler: 'passwordForgotten';
    args: {
      isMobileApp?: boolean;
    };
  };
}

export interface SetMode extends WalletRPC {
  request: {
    handler: 'setMode';
    args: {
      mode: 'signin' | 'signup';
    };
  };
}

export interface SignUpAdditionalScreen extends WalletRPC {
  request: {
    handler: 'signUpAdditionalScreen';
  };
}

export interface BackFromSignUpAdditionalScreen extends WalletRPC {
  request: {
    handler: 'backFromSignUpAdditionalScreen';
  };
}

export interface ExportForUser extends WalletRPC {
  request: {
    handler: 'exportForUser';
    args: {
      key: string | BufferSource;
    };
  };
  response: {
    result?: {
      userPrivateKey: WalletExport;
    };
  };
}

export interface WalletLoaded extends WalletRPC {
  request: {
    handler: 'walletLoaded';
  };
}

export const MessagingContext =
  createContext<IMessagingContext<Handler> | null>(null);

export const MessagingProvider = (props: Props<Handler>) =>
  Provider<Handler>(props);
